// React
import React, { useCallback, useEffect, useMemo, useState } from 'react'

// Next
import Link from 'next/link'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { GetStaticProps } from 'next'

// Components
import { Button } from '@/components/ui/button'
import { FileUpload } from '@/components/blocks/file-upload'
import { AnimatedInput } from '@/components/blocks/animated-input'
import MemeCaptionRegenerator from '@/components/memes/caption-regenerator'
import { PreviewImage } from '@/components/memes/templates/preview-image'
import Search from '@/components/memes/image-search'
import { GeneratedMeme } from '@/components/memes/templates/generated-meme'
import { CustomLoader } from '@/components/blocks/custom-loader'
import { LoginForm } from '@/components/blocks/login-form'
import TextSection from '@/components/website/core/TextSection'
import LinkedText from '@/components/website/core/LinkedText'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

// Utils
import { toBase64FromFile } from '@/utils/index'
import {
  useRemainingCredits,
  useSession,
  useRefreshUsedCredits,
  useSubscription,
  useUserDetails,
  useWatermark,
} from '@/utils/auth/user-context'
import { classNames, shuffleArray } from '@supermeme-ai/utils'
import { getFileNameFromUrl } from '@/utils/ui/meme/file'
import { generateThemedTemplateMeme } from '@/utils/ui/api-requests/generate-themed-template-meme'
import { generateThemedUploadedMeme } from '@/utils/ui/api-requests/generate-themed-uploaded-meme'
import { getLandingStaticProps } from '@/utils/ui/landing/get-static-props'
import { LinkedContent } from '@/utils/ui/types/linked-content'

// Types
import { MemeTemplate, Result } from '@supermeme-ai/types'

// External
import axios from 'axios'
import { compressAccurately } from 'image-conversion'
import { useWindowSize } from 'react-use'
import { toast } from 'react-hot-toast'
import { SearchIcon, SparklesIcon } from 'lucide-react'
import { fromBase64 } from 'js-base64'
import { useTranslation } from 'next-i18next'

const MemeGenerator: React.FC = () => {
  const { t } = useTranslation('routes/meme-generator')

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: t('meta.title'),
    description: t('meta.description'),
    applicationCategory: 'Meme Generator',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      category: 'Content Creation Tools',
      price: '0',
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.7',
      ratingCount: '18',
      reviewCount: '18',
      bestRating: '5',
      worstRating: '1',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Supermeme.ai',
      logo: {
        '@type': 'ImageObject',
        url: 'https://supermeme.ai/static/logo-big.png',
      },
    },
    featureList: [
      'AI-Powered Meme Generation',
      'Template Selection',
      'Custom Image Upload',
      'Caption Regeneration',
    ],
  }
  const router = useRouter()

  // context
  const session = useSession()
  const [userDetails] = useUserDetails()
  const subscription = useSubscription()
  const [watermark] = useWatermark()
  const remainingCredits = useRemainingCredits()
  const refreshRemainingCredits = useRefreshUsedCredits()

  // page state
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<Result[]>([])
  const [templateImageLoading, setTemplateImageLoading] = useState(false)
  const [showSearchDialog, setShowSearchDialog] = useState(false)
  const [showMemeCaptionEditor, setShowMemeCaptionEditor] = useState(false)
  const [theme, setTheme] = useState('')
  const [imageSrc, setImageSrc] = useState('')
  const [imageName, setImageName] = useState('')
  const [imageDescription, setImageDescription] = useState('')
  const [showLoginDialog, setShowLoginDialog] = useState(false)

  const { width: screenWidth } = useWindowSize()
  const maxDimension = useMemo(
    () => (screenWidth >= 500 ? 500 : window.innerWidth - 32),
    [screenWidth]
  )

  const generationDisabled = !subscription && remainingCredits <= 0

  const canGenerateMeme = useCallback(
    (
      validateTheme: boolean,
      validateImage: boolean,
      validateDescription: boolean
    ): boolean => {
      if (generationDisabled) return false
      if (validateTheme && !theme) {
        toast.error(t('errors.selectTheme'))
        return false
      }
      if (validateImage && !imageSrc) {
        toast.error(t('errors.selectTemplate'))
        return false
      }
      if (validateDescription && !imageDescription) {
        toast.error(t('errors.selectTemplate'))
        return false
      }
      return true
    },
    [generationDisabled, theme, imageSrc, imageDescription, t]
  )

  const generateMeme = useCallback(
    async (evt) => {
      evt?.preventDefault()
      if (!canGenerateMeme(true, true, false)) return

      setLoading(true)
      setResults([])

      try {
        const response =
          imageDescription && imageName
            ? await generateThemedTemplateMeme(
                session?.access_token,
                imageName,
                theme,
                maxDimension,
                userDetails?.meme_input_language,
                userDetails?.meme_output_language,
                imageDescription
              )
            : await generateThemedUploadedMeme(
                session?.access_token,
                imageSrc,
                theme,
                maxDimension,
                userDetails?.meme_input_language,
                userDetails?.meme_output_language
              )

        refreshRemainingCredits()
        setImageDescription(response?.imageCaption || '')
        setResults(response?.results || [])
      } catch {
        toast.error(t('errors.generation'))
      } finally {
        setLoading(false)
      }
    },
    [
      canGenerateMeme,
      session?.access_token,
      imageName,
      imageSrc,
      theme,
      maxDimension,
      userDetails,
      imageDescription,
      refreshRemainingCredits,
      t,
    ]
  )

  const handleImageUpload = useCallback(async (file: File) => {
    if (!file) return

    const compressedFile =
      file.size > 1000000 ? await compressAccurately(file, 600) : file
    const base64String = await toBase64FromFile(compressedFile)
    setImageDescription('')
    setImageSrc(base64String)
    setResults([])
  }, [])

  const handleTemplateSelection = useCallback(
    async (template: MemeTemplate) => {
      const imageFileName = getFileNameFromUrl(template.image_path)
      if (!!session) {
        const { data } = await axios.get<{
          base64String: string
          fileType: string
        }>(
          `/api/image/base64?imageName=${imageFileName}&uploadedTemplate=false`,
          {
            headers: {
              'Content-Type': 'application/json',
              token: session.access_token,
            },
          }
        )
        setImageSrc(`data:${data.fileType};base64,${data.base64String}`)
      } else {
        setImageSrc(template.image_path)
      }
      setImageName(imageFileName)
      setImageDescription(template.description)
      setShowSearchDialog(false)
      setResults([])
    },
    [session]
  )

  const memeDescriptionEditor = useMemo(
    () => (
      <Dialog
        open={showMemeCaptionEditor}
        onOpenChange={setShowMemeCaptionEditor}
      >
        <DialogTrigger asChild>
          <Button variant='link'>{t('editor.editButton')}</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogTitle>{t('editor.title')}</DialogTitle>
          <MemeCaptionRegenerator
            imageSrc={imageSrc}
            initialCaption={imageDescription}
            regenerateIsDisabled={generationDisabled}
            onRegenerateClick={async (modifiedDescription: string) => {
              setImageDescription(modifiedDescription)
              if (!canGenerateMeme(true, false, false)) return
              setShowMemeCaptionEditor(false)
              setResults([])
              generateMeme(null)
            }}
          />
        </DialogContent>
      </Dialog>
    ),
    [
      imageSrc,
      imageDescription,
      showMemeCaptionEditor,
      generationDisabled,
      canGenerateMeme,
      generateMeme,
      t,
    ]
  )

  const memeSearchButton = useMemo(
    () => (
      <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <DialogTrigger asChild>
          <Button
            variant='outline'
            className='border-2'
            style={{ padding: '2rem 3rem' }}
          >
            <SearchIcon className='mr-2 h-4 w-4' />
            <span>{t('imageSelection.searchButton')}</span>
          </Button>
        </DialogTrigger>
        <DialogContent className='max-w-7xl'>
          <Search session={session} onSelect={handleTemplateSelection} />
        </DialogContent>
      </Dialog>
    ),
    [session, showSearchDialog, handleTemplateSelection, t]
  )

  useEffect(() => {
    if (session) {
      const { template, uploadedTemplate } = router.query
      if (template) {
        setTemplateImageLoading(true)
        const memeTemplate: MemeTemplate = JSON.parse(
          fromBase64(template as string)
        )
        const imageFileName = getFileNameFromUrl(memeTemplate.image_path)
        axios
          .get<{ base64String: string; fileType: string }>(
            `/api/image/base64?imageName=${imageFileName}&uploadedTemplate=${uploadedTemplate ?? false}`,
            {
              headers: {
                'Content-Type': 'application/json',
                token: session.access_token,
              },
            }
          )
          .then((response) => {
            const { data } = response
            setImageName(imageFileName)
            setImageDescription(memeTemplate.description)
            setImageSrc(`data:${data.fileType};base64,${data.base64String}`)
          })
          .finally(() => {
            setTemplateImageLoading(false)
          })
      }
    }
  }, [session, router.query])

  useEffect(() => {
    if (!!session) {
      setShowLoginDialog(false)
    }
  }, [session])

  return (
    <>
      <Head>
        <title>{t('meta.title')}</title>
        <meta name='description' content={t('meta.description')} />
        <meta property='og:url' content='https://supermeme.ai/meme-generator' />
        <link rel='canonical' href='https://supermeme.ai/meme-generator' />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>
      <div
        className={classNames(
          'mx-auto md:container',
          results.length > 0 || !!imageSrc ? 'space-y-6' : 'space-y-24'
        )}
        onPaste={(event) => {
          const file = event.clipboardData.files[0]
          if (
            file &&
            ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'].includes(
              file.type
            )
          ) {
            handleImageUpload(file)
          }
        }}
      >
        <form
          onSubmit={(evt) => {
            evt.preventDefault()

            if (!session) {
              setShowLoginDialog(true)
              return
            }

            generateMeme(evt)
          }}
        >
          <div className='mx-auto max-w-4xl space-y-4 text-center'>
            <label
              htmlFor='theme'
              className='w-full text-center text-xl font-semibold lg:text-3xl'
            >
              {t('form.theme.label')}
            </label>

            <div className='flex flex-col items-start space-x-0 space-y-2 md:flex-row md:space-x-2 md:space-y-0'>
              <div className='flex w-full flex-col space-y-4'>
                <AnimatedInput
                  id='themed-meme'
                  name='themed-meme'
                  label={t('form.theme.input.label')}
                  placeholders={
                    t('themes', { returnObjects: true }) as string[]
                  }
                  value={theme}
                  required
                  maxLength={300}
                  buttonLabel={t('form.theme.input.button')}
                  buttonIcon={
                    <SparklesIcon
                      className='h-5 w-5 text-white'
                      aria-hidden='true'
                    />
                  }
                  buttonDisabled={!!session && (generationDisabled || loading)}
                  buttonType='submit'
                  loading={loading}
                  onChange={setTheme}
                />

                {(!!subscription || remainingCredits > 0) && (
                  <div className='mx-auto flex w-full max-w-2xl justify-between px-1'>
                    <p className='mt-1 w-full text-left text-xs text-gray-500'>
                      <button
                        type='button'
                        className='underline'
                        onClick={() =>
                          setTheme(
                            shuffleArray(
                              t('themes', { returnObjects: true }) as string[]
                            )[0]
                          )
                        }
                      >
                        {t('form.theme.input.trySample')}
                      </button>
                    </p>

                    <div className='flex'>
                      <p className='mt-1 w-[120px] whitespace-nowrap text-right text-xs text-gray-500'>
                        {t('form.theme.input.charCount', {
                          count: theme?.length ?? 0,
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {!!session && !subscription && remainingCredits <= 0 && (
                  <Link
                    className='mt-8 w-full max-w-full space-y-1 text-center text-sm'
                    href='/account/upgrade'
                  >
                    <span
                      className='text-center text-base font-bold text-indigo-600'
                      style={{ flex: 1 }}
                    >
                      {t('subscription.noCredits.text')}{' '}
                      <span className='font-bold text-indigo-600 underline'>
                        {t('subscription.noCredits.link')}
                      </span>{' '}
                      {t('subscription.noCredits.suffix')}
                    </span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </form>

        <div className='flex flex-col justify-center space-x-0 space-y-4 lg:flex-row lg:items-center lg:space-x-12 lg:space-y-0'>
          <FileUpload
            disabled={!!session && generationDisabled}
            onFileAccepted={handleImageUpload}
          />
          <span className='w-full text-center text-lg lg:w-auto'>
            {t('imageSelection.or')}
          </span>
          {memeSearchButton}
        </div>

        {imageSrc && (
          <div className='space-y-4'>
            {results.length === 0 && (
              <PreviewImage loading={loading} imageSrc={imageSrc} />
            )}
            {/* TODO: add this later */}
            {/* <div className='flex items-center justify-center'>
              {memeDescriptionEditor}
            </div> */}
          </div>
        )}

        {results.length === 0 && templateImageLoading && (
          <div className='flex items-center justify-center'>
            <CustomLoader />
          </div>
        )}

        {results.length > 0 && (
          <div className='grid grid-cols-1 gap-2 border-t border-gray-200 pt-6 lg:grid-cols-2 lg:gap-4'>
            {results.map((result) => (
              <div key={result.id} className='rounded-lg bg-gray-100 p-4'>
                <GeneratedMeme
                  initialCaptions={result.captions}
                  memeFormat={'image'}
                  watermark={watermark}
                  initialResultId={result.id}
                  imageSrc={imageSrc}
                  maxDimension={maxDimension}
                  initialHeader={result.header}
                  initialFooter={result.footer}
                  imageWidth={result.width}
                  imageHeight={result.height}
                  isStarredMeme={false}
                  starUnstarCallback={() => {}}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <hr className='my-12' />
      <div className='m-12'>
        <TextSection
          h2={t('about.title')}
          p={
            <LinkedText
              content={
                t('about.description', { returnObjects: true }) as LinkedContent
              }
            />
          }
        />

        <TextSection
          h3={t('about.features.title')}
          li={(
            t('about.features.list', { returnObjects: true }) as {
              title: string
              description: any
            }[]
          ).map((item) => (
            <>
              <strong>{item.title}:</strong>{' '}
              {typeof item.description === 'string' ? (
                item.description
              ) : (
                <LinkedText content={item.description} />
              )}
            </>
          ))}
        />

        <TextSection
          h3={t('howTo.title')}
          li={t('howTo.steps', { returnObjects: true }) as string[]}
        />
      </div>

      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent className='sm:max-w-[425px]'>
          <LoginForm isDialog={true} redirectUrl={router.asPath} />
        </DialogContent>
      </Dialog>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return getLandingStaticProps({
    locale,
    namespaces: [
      'common',
      'routes/meme-generator',
      'components/image-search',
      'components/login-form',
      'components/meme-editor',
      'components/raw-meme-template',
    ],
  })
}

export default MemeGenerator
